import { useAccessibility } from "../../../context/AccessibilityContext/AccessibilityContext";
import Block from "../../../components/Block/Block";
import FadeIn from "../../../components/FadeIn/FadeIn";
import Link from "../../../components/Link/Link";
import style from './educationblock.module.scss';
import { useEffect, useState, useMemo } from 'react';
import { fetchEducationInfo } from '../../../services/api';

const EducationBlock = () => {
    const { settings } = useAccessibility();
    const [educationData, setEducationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Accessibility-aware colors
    const backgroundColor = settings.contrast === 'default' ? 'rgba(255, 255, 255, 0.85)' : 'var(--bg-primary)';
    const textColor = settings.contrast === 'default' ? '#231f20' : 'var(--text-primary)';
    const linkColor = settings.contrast === 'default' ? '#1d3e8c' : 'var(--accent-color)';
    const strongColor = settings.contrast === 'default' ? '#1d3e8c' : 'var(--text-primary)';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchEducationInfo();
                setEducationData(response.data[0].attributes);
                console.log('Education data:', response.data[0].attributes);
            } catch (err) {
                console.error('Error fetching education data:', err);
                setError('Не удалось загрузить данные об образовании');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Format stages for display with better abbreviations
    const formatStages = (stages) => {
        if (!stages || stages.length === 0) return 'Не указано';
        
        const stageNames = {
            'НП': 'Начальная подготовка',
            'УП': 'Учебно-тренировочный этап',
            'УТ': 'Учебно-тренировочный этап',
            'ССМ': 'Совершенствование спортивного мастерства',
            'ВСМ': 'Высшее спортивное мастерства'
        };

        return stages.map(stage => (
            <span key={stage.id}>
                <abbr title={stageNames[stage.Stage] || stage.Stage}>{stage.Stage}</abbr>: {stage.TimePeriod}
            </span>
        )).reduce((prev, curr) => [prev, ', ', curr]);
    };

    // File link component
    const FileLink = ({ file, label, fallbackUrl, fallbackExt }) => {
        if (file?.data?.attributes?.url) {
            return (
                <Link
                    href={`${file.data.attributes.url}`}
                    children={`${label} (${file.data.attributes.ext || 'PDF'})`}
                    color={linkColor}
                    aria-label={`Скачать документ: ${label}`}
                    target="_blank"
                    rel="noopener noreferrer"
                />
            );
        }
        if (fallbackUrl) {
            return (
                <Link
                    href={fallbackUrl}
                    children={`${label} (${fallbackExt})`}
                    color={linkColor}
                    aria-label={`Скачать документ: ${label}`}
                    target="_blank"
                    rel="noopener noreferrer"
                />
            );
        }
        return null;
    };

    // Получаем sports данные с проверкой структуры
    const sportsData = educationData?.sports?.data || [];

    // Memoize education sections
    const educationSections = useMemo(() => [
        {
            id: 'programs',
            title: 'Реализуемые образовательные программы',
            content: (
                <>
                    <FileLink 
                        file={educationData?.SportProgramsFile}
                        label="Сведения о реализуемых образовательных программах"
                        fallbackUrl="https://drive.google.com/file/d/1sbemTZho4gyzQTi7kBi97akUMqfAijfH/view?usp=sharing"
                        fallbackExt="PDF"
                    />
                    <br /><br />
                    <strong style={{ color: strongColor }}>Дополнительные образовательные программы спортивной подготовки:</strong>
                    <ul className={style.list} aria-label="Программы спортивной подготовки">
                        {sportsData.map((sport) => (
                            <li key={sport.id}>
                                {/* Исправлено: обращаемся напрямую к sport.ProgramFile */}
                                <FileLink 
                                    file={sport.ProgramFile}
                                    label={`Программа по виду спорта «${sport.Title}»`}
                                />
                                <ul className={style.nestedList}>
                                    <li>Общая физическая подготовка</li>
                                    <li>Специальная физическая подготовка</li>
                                    <li>Участие в спортивных соревнованиях</li>
                                    <li>Техническая подготовка</li>
                                    <li>Тактическая, теоретическая, психологическая подготовка</li>
                                    <li>Инструкторская и судейская практика</li>
                                </ul>
                                <strong>Этапы подготовки:</strong> {formatStages(sport.Stages)}
                            </li>
                        ))}
                    </ul>
                </>
            )
        },
        {
            id: 'education-level',
            title: 'Сведения об уровне образования',
            content: (
                <>
                    <p>Реализуемые в ГБУ ДО КК «СШОР «Самбо» относятся к следующему виду образовательных программ:</p>
                    <ul className={style.list}>
                        <li>Дополнительные общеобразовательные программы в области физической культуры и спорта (ст. 84 Закона об Образовании в РФ)</li>
                    </ul>
                    <p>Требования к уровню образования поступающих отсутствуют.</p>
                </>
            )
        },
        {
            id: 'education-form',
            title: 'Сведения о форме обучения',
            content: (
                <>
                    <p>При реализации образовательных программ применяются следующие формы обучения:</p>
                    <ul className={style.list}>
                        <li>Учебно-тренировочные (в т.ч. теоретические) занятия</li>
                        <li>Работа по индивидуальным планам</li>
                        <li>Учебно-тренировочные мероприятия (сборы)</li>
                        <li>Участие в спортивных соревнованиях</li>
                        <li>Инструкторская и судейская практика</li>
                        <li>Медико-восстановительные мероприятия</li>
                        <li>Тестирование и контроль</li>
                        <li>Система спортивного отбора и спортивной ориентации</li>
                    </ul>
                </>
            )
        },
        {
            id: 'education-term',
            title: 'Сведения о нормативном сроке обучения',
            content: (
                <>
                    <ul className={style.list}>
                        {sportsData.map((sport) => (
                            <li key={`term-${sport.id}`}>
                                <strong>{sport.Title}:</strong> {formatStages(sport.Stages)}
                            </li>
                        ))}
                    </ul>
                    <p>*НП - начальная подготовка; УТ - учебно-тренировочный этап; ССМ - совершенствование спортивного мастерства; ВСМ - высшее спортивное мастерство</p>
                </>
            )
        },
        {
            id: 'language',
            title: 'Сведения о языках образования',
            content: (
                <>
                    <p>Образовательный процесс осуществляется на русском языке</p>
                    <FileLink 
                        file={educationData?.LanguageFile || educationData?.StateAssignmentFile}
                        label="Положение о языке образования"
                        fallbackUrl="https://drive.google.com/file/d/1IqtAXlBVMH7mgZvesydUc_KZxx0Ys_vi/view?usp=sharing"
                        fallbackExt="PDF"
                    />
                </>
            )
        },
        {
            id: 'students-count',
            title: 'Численность обучающихся',
            content: (
                <>
                    <p><strong>Всего обучающихся:</strong> {educationData?.TotalStudents || 1480} человек</p>
                    <p><strong>За счет бюджетных ассигнований:</strong> {educationData?.BudgetStudents || 1486} человек</p>
                    <p><strong>Иностранные граждане:</strong> {educationData?.ForeignStudents || 0} человек</p>
                    <FileLink 
                        file={educationData?.GovermentTaskFile || educationData?.StateAssignmentFile}
                        label="Государственное задание"
                        fallbackUrl="https://drive.google.com/file/d/1gNJX9VVkO1hOW77OzdtfwpUAt6Bn39hv/view"
                        fallbackExt="PDF"
                    />
                </>
            )
        },
        {
            id: 'other-info',
            title: 'Иные сведения',
            content: (
                <>
                    <p>Сведения о научной деятельности отсутствуют, так как не предусмотрены видом реализуемых образовательных программ.</p>
                    <p>Сведения о трудоустройстве выпускников отсутствуют, так как не предусмотрены видом реализуемых образовательных программ.</p>
                </>
            )
        }
    ], [educationData, sportsData, linkColor, strongColor]);

    if (loading) return (
        <Block
            flexDirection='column'
            justifyContent='start'
            alignItems='start'
            width="75%"
            height='200px'
            background={backgroundColor}
            header='ОБРАЗОВАНИЕ'
            headerColor={textColor}
        >
            <div>Загрузка данных об образовании...</div>
        </Block>
    );
    
    if (error) return (
        <Block
            flexDirection='column'
            justifyContent='start'
            alignItems='start'
            width="75%"
            height='200px'
            background={backgroundColor}
            header='ОБРАЗОВАНИЕ'
            headerColor={textColor}
        >
            <div>{error}</div>
        </Block>
    );
    
    if (!educationData) return (
        <Block
            flexDirection='column'
            justifyContent='start'
            alignItems='start'
            width="75%"
            height='200px'
            background={backgroundColor}
            header='ОБРАЗОВАНИЕ'
            headerColor={textColor}
        >
            <div>Данные не найдены</div>
        </Block>
    );

    return (
        <Block
            flexDirection='column'
            justifyContent='start'
            alignItems='start'
            width="75%"
            height='fit-content'
            background={backgroundColor}
            header='ОБРАЗОВАНИЕ'
            headerColor={textColor}
            id='education'
            aria-label="Информация об образовательной деятельности"
            role="region"
        >
            <FadeIn>
                <div className={style.content}>
                    <div className={style.wrapperData} role="list">
                        {educationSections.map((section) => (
                            <section 
                                key={section.id}
                                className={style.row}
                                aria-labelledby={`${section.id}-title`}
                                role="listitem"
                            >
                                <h4 
                                    id={`${section.id}-title`}
                                    className={style.header}
                                    style={{ color: textColor }}
                                >
                                    {section.title}
                                </h4>
                                <div 
                                    className={style.text}
                                    style={{ color: textColor }}
                                >
                                    {section.content}
                                </div>
                            </section>
                        ))}
                    </div>
                </div>
            </FadeIn>
        </Block>
    )
}

export default EducationBlock;